/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { forwardRef } from 'react';
import type { ChangeEvent, FC, ReactElement, ReactNode, Ref } from 'react';
import { Suspense, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { Tooltip } from '@mui/material';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Typography,
  styled,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon, InfoOutlined } from '@mui/icons-material';
import { RuleStatus } from './';
import type { MenuItemModifiedProps, ActionsTypeParamsEntry, ConditionIndexProps } from '../facebook';
import type { RuleEntry, TaskIndexProps, VariantsOnTaskIndexProps, RuleAction } from '../types';
import {
  AltRoute as AltRouteIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreOutlined,
} from '@mui/icons-material';
import { useAnchorMenu, useCurrentNetwork } from 'hooks';
import RuleCommonAutocomplete from './RuleCommonAutocomplete';
import { EXLUDED_ACTIONS } from '../constants';
import { getReferencesOptions, facebookActionTypesOptions } from '../api';
import { tasksFormValidation } from '../utils';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import type { ActionsTypeEntry } from '../api';
import { useFormContext } from 'react-hook-form';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { RuleTypeMutation } from '../types';
import { useRuleNavigation } from '../hooks';
import useCurrentTasksFormStore from '../hooks/useCurrentTasksFormStore';
import scrollIntoView from '../utils/scrollIntoView';
import getActionLevelTooltip from '../utils/getActionLevelTooltip';
import useRulePageMode from '../hooks/useRulePageMode';

export const RuleEditAutoCompleteStyled = styled(RuleCommonAutocomplete)({
  '& .MuiInputBase-root': { minHeight: 36, maxHeight: 36, border: 'none' },
  '& .MuiOutlinedInput-root fieldset': { border: 'none', minHeight: 36, maxHeight: 36, top: 0 },
  '&.MuiAutocomplete-listbox': {
    wordBreak: 'break-all',
  },
  '& .MuiAutocomplete-option': {
    padding: '6px 16px',
    width: '100%',
    wordBreak: 'break-all',
    lineHeight: 1.25,
    boxSizing: 'border-box',
  },
});

export const ConditionVariantsContainerStyled = styled(Box)({
  display: 'flex',
  gap: '10px',
  width: '100%',
  alignItems: 'start',
  justifyContent: 'start',
  flexDirection: 'column',
});

type RuleEditTitle = Pick<RuleEntry, 'name' | 'state' | 'status'> & { mode: RuleTypeMutation };
export const RuleEditTitle: FC<RuleEditTitle> = ({ name, state, status, mode }) => {
  const {
    formState: { isDirty },
  } = useFormContext();

  const { ruleListURL } = useRuleNavigation();

  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: 1,
        [t.breakpoints.down('sm')]: {
          pt: 2,
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <Link to={ruleListURL} style={{ display: 'flex', alignItems: 'center' }}>
          <ArrowBackIcon sx={(t) => ({ color: t.palette.action.active })} />
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 1 }}>
          {isDirty || mode === 'duplicate' ? (
            <Typography variant="h3" sx={{ p: 0, wordBreak: 'break-all' }}>
              {mode === 'edit' && 'Edit rule'}
              {mode === 'duplicate' && 'Duplicate rule'}
            </Typography>
          ) : (
            <>
              <RuleStatus status={(status === 'active' ? state : status) as never} />
              <Typography variant="h3" sx={{ p: 0, wordBreak: 'break-all' }}>
                {name}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      {mode === 'duplicate' && (
        <Typography
          variant="caption"
          sx={(t) => ({
            p: 0,
            wordBreak: 'break-all',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: t.palette.text.secondary,
          })}
        >
          <InfoOutlined sx={(t) => ({ color: 'rgba(61, 90, 254, 1)', fontSize: t.typography.body2.fontSize })} />
          There cannot be two rules with the same name and conditions. Make changes to save new rule.
        </Typography>
      )}
    </Box>
  );
};

export const TabViewContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 500, // we will skip this value in the future
  paddingTop: 16,
  paddingBottom: 16,
});

export const LabelFormSection: FC<{ label: string; icon: ReactElement }> = ({ label, icon }) => {
  return (
    <Chip
      color="primary"
      label={label}
      icon={icon}
      sx={(t) => ({ py: 2, gap: 0.5, fontWeight: 400, fontSize: t.typography.caption.fontSize })}
    />
  );
};

const VerticalDividerStyled = styled(Box)(({ theme: t }) => {
  return {
    position: 'absolute',
    zIndex: 2,
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    background: t.palette.primary.main,
    borderRadius: 2,
    '&::before': {
      content: '""',
      position: 'absolute',
      zIndex: 2,
      top: 0,
      bottom: 0,
      margin: 'auto',
      left: -11,
      width: 26,
      height: 26,
      background: t.palette.primary.main,
      borderRadius: 13,
      border: `4px solid white`,
    },
  };
});

export const VerticalDivider = () => {
  return (
    <VerticalDividerStyled>
      <ArrowForwardIcon
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: -7,
          margin: 'auto',
          zIndex: 3,
          color: 'white',
          fontSize: 20,
        }}
      />
    </VerticalDividerStyled>
  );
};

export const ConditionFormContainer: FC<{
  title: ReactElement;
  sx?: SxProps<Theme>;
  children?: ReactNode;
  indexCondition: number;
  type?: 'action' | 'condition' | 'variant';
}> = ({ title, sx, children, indexCondition, type = 'condition' }) => {
  return (
    <Box
      id={`${type}-${indexCondition}`}
      sx={(t) => ({
        width: '100%',
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'start',
        flexDirection: 'column',
        border: `1px solid ${`rgba(0, 8, 46, 0.12)`}`,
        borderRadius: t.borderRadius.md,
        '.visiblyDeleteButton': { visibility: 'hidden' },
        '&:hover .visiblyDeleteButton': { visibility: 'visible' },
      })}
    >
      <Box
        sx={{
          height: 36,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          py: 2,
          px: 1,
          gap: 2,
          backgroundColor: 'rgba(41, 121, 255, 0.04)',
          borderBottom: `1px solid rgba(41, 121, 255, 0.08)`,
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'start',
          flexDirection: 'column',
          width: '100%',
          px: 1.5,
          py: 2,
          ...(sx ?? {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const ConditionVariantForm: FC<{ title: string; sx?: SxProps<Theme>; children?: ReactNode }> = ({
  title,
  sx,
  children,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'start',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          pb: 1.5,
          px: 0,
        }}
      >
        <Typography variant="subtitle2" sx={(t) => ({ color: t.palette.primary.main, fontWeight: 500 })}>
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'start',
          flexDirection: 'column',
          width: '100%',
          '&:not(::last-type)': {
            pb: 1,
          },
          ...(sx ?? {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const DuplicateTaskButton: FC<TaskIndexProps> = observer(({ indexTask }) => {
  const { onShowAlert } = useEnqueueSnackbar();
  const tasksFormStore = useCurrentTasksFormStore();
  const { isAddTaskAvailable } = tasksFormValidation({ tasksForm: tasksFormStore.tasksForm, indexTask });
  const handleDuplicateTask = () => {
    tasksFormStore.duplicateTask(indexTask);
    scrollIntoView(`#task-item-${indexTask + 1}`, () => {
      onShowAlert('Task duplicated!');
    });
  };

  return (
    <IconButton
      size="medium"
      sx={{ p: 0, height: 24, display: 'flex', alignItems: 'end' }}
      onClick={handleDuplicateTask}
      className="visibleByHover"
      disabled={!isAddTaskAvailable}
    >
      <ContentCopyIcon sx={{ fontSize: 22 }} />
    </IconButton>
  );
});

export const AddTaskButton = observer(() => {
  const tasksFormStore = useCurrentTasksFormStore();
  const { isAddTaskAvailable } = tasksFormValidation({ tasksForm: tasksFormStore.tasksForm });

  const handleAddTask = useCallback(() => {
    tasksFormStore.addTask();
  }, [tasksFormStore]);

  return (
    <Button
      size="medium"
      startIcon={<AddIcon />}
      variant="text"
      onClick={handleAddTask}
      disabled={!isAddTaskAvailable || tasksFormStore?.propsRestriction?.disabled}
    >
      Add task
    </Button>
  );
});

const MenuItemRef = forwardRef((props: MenuItemModifiedProps, ref: Ref<any>) => {
  return (
    <Tooltip title={props.tooltip} placement={'right'} arrow>
      <MenuItem {...props} ref={ref}>
        {props.children}
      </MenuItem>
    </Tooltip>
  );
});
MenuItemRef.displayName = 'MenuItemRef';

export const AddActionsButton: FC<{ indexTask: number; indexVariant?: number; actions: RuleAction[] }> = observer(
  ({ indexTask, indexVariant, actions }) => {
    const { watch } = useFormContext();
    const ruleLevel = watch('level') as string;
    const tasksFormStore = useCurrentTasksFormStore();

    const selectedActionIds = actions?.filter((item) => item.action).map((item) => item.action);

    const { isAddActionAvailable } = tasksFormValidation({ tasksForm: tasksFormStore.tasksForm, indexTask });
    const { handleCloseMenu, handleOpenMenu, anchor, isOpen } = useAnchorMenu();

    const { data: actionsType } = useSuspenseQuery({
      ...facebookActionTypesOptions,
      select: (data: ActionsTypeEntry[]) => {
        return data.map((a) => ({
          ...a,
          disabled: a.level?.toLowerCase() !== ruleLevel?.toLowerCase() && a.level !== 'all',
        }));
      },
    });

    const filteredActionTypes = useMemo(() => {
      return actionsType
        .filter((item) => !selectedActionIds?.some((id) => id === item.name))
        .filter((a) => !EXLUDED_ACTIONS.includes(a.name));
      // selectedActionIds as useEffect do not track mobx proxy object changes
    }, [actionsType, selectedActionIds]);

    const handleAddAction = (action: string) => {
      tasksFormStore.addAction({ indexTask, indexVariant, action });
      handleCloseMenu();
    };

    return (
      <>
        <Button
          size="medium"
          startIcon={<AddIcon />}
          variant="text"
          onClick={handleOpenMenu}
          disabled={!isAddActionAvailable}
          endIcon={
            <ExpandMoreOutlined
              sx={(t) => ({
                fontSize: 14,
                color: isAddActionAvailable ? t.palette.primary.main : t.palette.grey[300],
                transform: `rotate(${isOpen ? '180deg' : '0deg'})`,
                transition: 'transform .1s',
              })}
            />
          }
        >
          Add action
        </Button>
        <Menu anchorEl={anchor} open={isOpen} onClose={handleCloseMenu} sx={{ maxHeight: '470px' }}>
          {filteredActionTypes.map((item: ActionsTypeParamsEntry) => (
            <Tooltip
              key={`Task:${indexTask}.ActionType:${item.id}:${item.name}`}
              title={item.disabled ? getActionLevelTooltip(item.level) : null}
              placement={'right'}
              arrow
            >
              <MenuItem
                disableRipple={item.disabled}
                disableTouchRipple={item.disabled}
                sx={(t) => ({
                  display: 'flex',
                  justifyContent: item.disabled ? 'space-between' : 'start',
                  alignItems: 'center',
                  color: item.disabled ? t.palette.text.disabled : t.palette.grey[900],
                  cursor: item.disabled ? 'default' : 'pointer',
                  '&.MuiMenuItem-root:hover': item.disabled ? { backgroundColor: 'transparent' } : {},
                })}
                onClick={() => {
                  if (item.disabled) return;
                  handleAddAction(item.name);
                }}
              >
                <Typography variant={'body2'} sx={{ p: 0, m: 0 }}>
                  {item.name}
                </Typography>
                {item.disabled && <InfoOutlined color={'inherit'} />}
              </MenuItem>
            </Tooltip>
          ))}
        </Menu>
      </>
    );
  }
);

export const DeleteTaskButton: FC<TaskIndexProps> = observer(({ indexTask }) => {
  const tasksFormStore = useCurrentTasksFormStore();
  const { onShowAlert, onShowInfoAlert } = useEnqueueSnackbar();
  const handleDeleteTask = () => {
    const handleDeletion = tasksFormStore.deleteTask(indexTask);
    scrollIntoView(`#task-item-${indexTask - 1}`);

    onShowAlert('Task deleted', {
      delayAlert: 3500,
      onUndo: () => {
        handleDeletion.undo();
        onShowInfoAlert('Task recovered', {
          isPending: false,
          delayAlert: 3500,
        });

        scrollIntoView(`#task-item-${indexTask}`);
      },
    });
  };

  return (
    <IconButton size="medium" sx={{ p: 0, height: 24 }} onClick={handleDeleteTask} className="visibleByHover">
      <DeleteIcon sx={{ fontSize: 28, mb: -0.3 }} />
    </IconButton>
  );
});

export const AddConditionButton: FC<{ indexTask: number }> = observer(({ indexTask }) => {
  const { isRead } = useRulePageMode();
  const tasksFormStore = useCurrentTasksFormStore();
  const { isAddConditionAvailable } = tasksFormValidation({ tasksForm: tasksFormStore.tasksForm, indexTask });
  const { handleCloseMenu, handleOpenMenu, anchor, isOpen } = useAnchorMenu();
  const addSingleCondition = () => {
    handleCloseMenu();
    tasksFormStore.addSingleCondition(indexTask);
  };

  const addComparisonCondition = () => {
    handleCloseMenu();
    tasksFormStore.addComparisonCondition(indexTask);
  };

  return (
    !isRead && (
      <>
        <Button
          size="medium"
          startIcon={<AddIcon />}
          variant="text"
          onClick={handleOpenMenu}
          disabled={!isAddConditionAvailable || tasksFormStore?.propsRestriction?.disabled}
          endIcon={
            <ExpandMoreOutlined
              sx={(t) => ({
                fontSize: 14,
                color: isAddConditionAvailable ? t.palette.primary.main : t.palette.grey[300],
                transform: `rotate(${isOpen ? '180deg' : '0deg'})`,
                transition: 'transform .1s',
              })}
            />
          }
        >
          Add condition
        </Button>
        <Menu anchorEl={anchor} open={isOpen} onClose={handleCloseMenu}>
          <MenuItem onClick={addSingleCondition}>Single metric</MenuItem>
          <MenuItem onClick={addComparisonCondition}>Metric compared to metric</MenuItem>
        </Menu>
      </>
    )
  );
});

export const DeleteCondition: FC<{ indexTask: number; indexCondition: number }> = observer(
  ({ indexTask, indexCondition }) => {
    const tasksFormStore = useCurrentTasksFormStore();
    const { onShowAlert, onShowInfoAlert } = useEnqueueSnackbar();

    const handleDeleteCondition = () => {
      const handleDeletion = tasksFormStore.deleteCondition(indexTask, indexCondition);

      onShowAlert('Condition deleted', {
        delayAlert: 3500,
        onUndo: () => {
          handleDeletion.undo();
          onShowInfoAlert('Condition recovered', {
            isPending: false,
            delayAlert: 3500,
          });
        },
      });
    };

    return (
      <IconButton
        size="medium"
        sx={{ p: 0, height: 24 }}
        onClick={handleDeleteCondition}
        className="visiblyDeleteButton"
      >
        <DeleteIcon sx={{ fontSize: 24, mb: -0.3 }} />
      </IconButton>
    );
  }
);

export const DeleteVariantOnTask: FC<VariantsOnTaskIndexProps> = observer(({ indexTask, indexVariant }) => {
  const { onShowAlert, onShowInfoAlert } = useEnqueueSnackbar();
  const tasksFormStore = useCurrentTasksFormStore();

  const handleDelete = () => {
    const handleDeletion = tasksFormStore.deleteVariantOnTask(indexTask, indexVariant);

    onShowAlert('Variant of task deleted', {
      delayAlert: 3500,
      onUndo: () => {
        handleDeletion.undo();
        onShowInfoAlert('Variant of task recovered', {
          isPending: false,
          delayAlert: 3500,
        });
      },
    });
  };

  return (
    <IconButton size="medium" sx={{ p: 0, height: 24 }} onClick={handleDelete} className="visiblyDeleteButton">
      <DeleteIcon sx={{ fontSize: 24, mb: -0.3 }} />
    </IconButton>
  );
});

export const ConditionFormContainerTitle: FC<
  ConditionIndexProps & {
    title: string;
    subTitle?: string;
    showDeleteIcon?: boolean;
  }
> = ({ title, subTitle, indexCondition, indexTask, showDeleteIcon = true }) => {
  const { isRead } = useRulePageMode();
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: isRead ? 'start' : 'space-between', flexGrow: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <AltRouteIcon sx={(t) => ({ color: t.palette.primary.main, fontSize: t.typography.h4 })} />
          <Typography variant="subtitle2" sx={(t) => ({ color: t.palette.primary.main, fontWeight: 500 })}>
            {title}
          </Typography>
        </Box>
        {subTitle && (
          <Typography variant="body2" sx={(t) => ({ color: t.palette.text.primary, fontWeight: 400 })}>
            {subTitle}
          </Typography>
        )}
      </Box>
      {!isRead && showDeleteIcon && <DeleteCondition indexCondition={indexCondition} indexTask={indexTask} />}
    </Box>
  );
};

export const TaskOperator: FC<Pick<ConditionIndexProps, 'indexTask'>> = observer(({ indexTask }) => {
  const { isRead } = useRulePageMode();
  const tasksFormStore = useCurrentTasksFormStore();
  const handleChangeOperator = (e: ChangeEvent<HTMLInputElement>) => {
    tasksFormStore.changeOperator(indexTask, e.target.value);
  };

  return (
    <RadioGroup
      onChange={handleChangeOperator}
      value={tasksFormStore.getOperator(indexTask)}
      name={`ConditionGroup.${indexTask}`}
      sx={{ display: 'flex', flexDirection: 'row' }}
    >
      <FormControlLabel value="AND" control={<Radio />} label="AND" disabled={isRead} />
      <FormControlLabel value="OR" control={<Radio />} label="OR" disabled={isRead} />
    </RadioGroup>
  );
});

const ConditionOperatorSelect: FC<{ onChange: (e: SelectChangeEvent) => void; value?: string }> = ({
  onChange,
  value,
}) => {
  const { isRead } = useRulePageMode();
  const tasksFormStore = useCurrentTasksFormStore();

  const formatName = useCallback((name: string) => {
    const formattedName = name.replace(/_/g, ' ').toLowerCase();
    return formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
  }, []);
  const network = useCurrentNetwork();
  const { data: operators } = useSuspenseQuery({
    ...getReferencesOptions(network),
    select: (data) => data.conditions.operators.map(({ id, name }) => ({ id, name: formatName(name) })),
  });

  return (
    <Select
      variant="outlined"
      value={value}
      onChange={onChange}
      sx={(t) => ({
        '&.MuiInputBase-root, &.MuiInputBase-root fieldset': { border: 'none' },
        '&.MuiInputBase-root': {
          minHeight: 24,
          maxHeight: 24,
          borderRadius: t.borderRadius.md,
          backgroundColor: t.palette.grey[50],
          mt: 0.25,
        },
      })}
      disabled={tasksFormStore?.propsRestriction?.disabled ?? isRead}
    >
      {operators.map(({ id, name }) => (
        <MenuItem key={id} value={id}>
          {name}
        </MenuItem>
      ))}
    </Select>
  );
};

export const ConditionOperator: FC<{
  onChange: (e: SelectChangeEvent) => void;
  value?: string;
  isSingleCondition?: boolean;
}> = observer(({ value, onChange, isSingleCondition }) => {
  const iconMapper = useRef<Record<string, string>>({
    LESS_THAN: '<',
    GREATER_THAN: '>',
    DOES_NOT_EQUAL: '≠',
    EQUAL: '=',
    GREATER_THAN_OR_EQUAL: '≥',
    LESS_THAN_OR_EQUAL: '≤',
    default: '.',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, pt: 3, pb: 2.5 }}>
      <Box
        sx={(t) => ({
          position: 'relative',
          '&:after': !isSingleCondition
            ? {
                content: "''",
                position: 'absolute',
                zIndex: 1,
                top: -16,
                bottom: -18,
                left: 13,
                width: 2,
                background: t.palette.divider,
                borderRadius: 2,
              }
            : {},
        })}
      >
        <Box
          sx={(t) => ({
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            minWidth: 28,
            maxWidth: 28,
            height: 28,
            borderRadius: 14,
            backgroundColor: t.palette.grey[50],
            border: '4px solid white',
          })}
        >
          <Typography component="span" sx={(t) => ({ fontSize: t.typography.body1.fontSize, lineHeight: 0 })}>
            {iconMapper.current[value ?? 'default']}
          </Typography>
        </Box>
      </Box>

      <Suspense
        fallback={
          <Box
            sx={{
              width: '140px',
              height: 24,
              mt: 0.25,
            }}
          >
            <Skeleton
              width={'100%'}
              height={24}
              sx={(t) => ({
                transform: 'translateY(0)',
                borderRadius: t.borderRadius.md,
                backgroundColor: t.palette.grey[50],
                m: 0,
              })}
            />
          </Box>
        }
      >
        <ConditionOperatorSelect value={value} onChange={onChange} />
      </Suspense>
    </Box>
  );
});
